import { createSlice } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from 'src/app/redux/createAction';
import { apiRequest } from 'src/shared/api/api';
import { RequestStatus, UrlAPI } from 'src/shared/api/types';
import {
	BenchmarkPeers,
	CompanyDatasetDTO,
	PeerCompanyMetadataDTO,
	GeneralDTO,
	PeerCompanyDatasetDTO,
	PeerCompanyDatasetRequestDTO,
	Peers,
	PeerCompanyPerformanceDTO,
} from './type';

const NAME = 'peercompany';

const getMetadata = createAppAsyncThunk(`${NAME}/getMetadata`, async (_, thunkAPI) => {
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<PeerCompanyMetadataDTO>({
		url: `${UrlAPI.peerCompany}/metadata`,
		thunkAPI,
		signal,
	});
});

const getGeneral = createAppAsyncThunk(`${NAME}/getGeneral`, async (arg: { params: Peers }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<GeneralDTO[]>({
		url: `${UrlAPI.peerCompany}/general`,
		params,
		thunkAPI,
		signal,
	});
});

const getPerformance = createAppAsyncThunk(`${NAME}/getPerformance`, async (arg: { params: BenchmarkPeers }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<PeerCompanyPerformanceDTO[]>({
		url: `${UrlAPI.peerCompany}/performance`,
		params,
		thunkAPI,
		signal,
	});
});

// Performance indicators
const getDataset = createAppAsyncThunk(`${NAME}/getDataset`, async (arg: { payload: PeerCompanyDatasetRequestDTO }, thunkAPI) => {
	const { payload } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.postRequest<PeerCompanyDatasetDTO[]>({
		url: `${UrlAPI.peerCompany}/dataset`,
		payload,
		thunkAPI,
		signal,
	});
});

const getFullDataset = createAppAsyncThunk(`${NAME}/getFullDataset`, async (arg: { payload: PeerCompanyDatasetRequestDTO }, thunkAPI) => {
	const { payload } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.postRequest<CompanyDatasetDTO[]>({
		url: `${UrlAPI.peerCompany}/fulldataset`,
		payload,
		thunkAPI,
		signal,
	});
});

// * Reducer
interface State {
	metadata: {
		data: PeerCompanyMetadataDTO | null;
		status: RequestStatus;
	};
	general: {
		data: GeneralDTO[];
		status: RequestStatus;
	};
	performance: {
		data: PeerCompanyPerformanceDTO[] | null;
		status: RequestStatus;
	};
	dataset: {
		data: PeerCompanyDatasetDTO[];
		booleanDatapointIds: number[];
		numericDatapointIds: number[];
		status: RequestStatus;
	};
	fullDataset: {
		data: CompanyDatasetDTO[];
		status: RequestStatus;
	};
	pdfImages: Record<string, string> | null;
	imagesLoading: number;
}

export const initialState: State = {
	metadata: {
		data: null,
		status: RequestStatus.still,
	},
	general: {
		data: [],
		status: RequestStatus.still,
	},
	performance: {
		data: null,
		status: RequestStatus.still,
	},
	dataset: {
		data: [],
		booleanDatapointIds: [],
		numericDatapointIds: [],
		status: RequestStatus.still,
	},
	fullDataset: {
		data: [],
		status: RequestStatus.still,
	},
	pdfImages: null,
	imagesLoading: 0,
};

export const slice = createSlice({
	name: NAME,
	initialState,
	reducers: {
		clearPdfImages: (state) => {
			state.pdfImages = null;
			state.imagesLoading = 0;
		},
		clearBenchmarkPage: (state) => {
			state.general = initialState.general;
			state.performance = initialState.performance;
			state.dataset = initialState.dataset;
			state.fullDataset = initialState.fullDataset;
			state.pdfImages = initialState.pdfImages;
			state.imagesLoading = initialState.imagesLoading;
		},
		imageLoadStart: (state) => {
			state.imagesLoading = state.imagesLoading + 1;
		},
		imageSave: (state, action: { payload: { title: string; image: string } }) => {
			const { title, image } = action.payload;

			state.pdfImages = {
				...state.pdfImages,
				[title]: image,
			};
			state.imagesLoading = state.imagesLoading - 1;
		},
		removeBooleanDatasetItem: (state, action: { payload: { id: number } }) => {
			state.dataset.booleanDatapointIds = state.dataset.booleanDatapointIds.filter((id) => Number(id) !== Number(action.payload.id));
			state.dataset.data = state.dataset.data.map((item) => ({
				...item,
				booleanDataPoints: item.booleanDataPoints ? item.booleanDataPoints.filter((dataPoint) => Number(dataPoint.id) !== Number(action.payload.id)) : null,
			}));
			state.fullDataset.data = state.fullDataset.data.map((item) => ({
				...item,
				dataPoints: item.dataPoints ? item.dataPoints.filter((dataPoint) => Number(dataPoint.id) !== Number(action.payload.id)) : [],
			}));
		},
		removeNumericDatasetItem: (state, action: { payload: { id: number } }) => {
			state.dataset.numericDatapointIds = state.dataset.numericDatapointIds.filter((id) => Number(id) !== Number(action.payload.id));
			state.dataset.data = state.dataset.data.map((item) => ({
				...item,
				numericDataPoints: item.numericDataPoints ? item.numericDataPoints.filter((dataPoint) => Number(dataPoint.id) !== Number(action.payload.id)) : null,
			}));
			state.fullDataset.data = state.fullDataset.data.map((item) => ({
				...item,
				dataPoints: item.dataPoints ? item.dataPoints.filter((dataPoint) => Number(dataPoint.id) !== Number(action.payload.id)) : [],
			}));
		},
		addBooleanDatasetItem: (state, action: { payload: { id: number } }) => {
			if (!state.dataset.booleanDatapointIds.find((id) => Number(id) === Number(action.payload.id))) {
				state.dataset.booleanDatapointIds.push(action.payload.id);
			}
		},
		addNumericDatasetItem: (state, action: { payload: { id: number } }) => {
			if (!state.dataset.numericDatapointIds.find((id) => Number(id) === Number(action.payload.id))) {
				state.dataset.numericDatapointIds.push(action.payload.id);
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getMetadata.pending, (state) => {
			state.metadata.status = RequestStatus.loading;
		});
		builder.addCase(getMetadata.fulfilled, (state, action) => {
			state.metadata.data = action.payload;
			state.metadata.status = RequestStatus.still;
		});
		builder.addCase(getMetadata.rejected, (state) => {
			state.metadata.status = RequestStatus.failed;
		});

		builder.addCase(getGeneral.pending, (state) => {
			state.general.status = RequestStatus.loading;
		});
		builder.addCase(getGeneral.fulfilled, (state, action) => {
			state.general.data = action.payload;
			state.general.status = RequestStatus.still;
		});
		builder.addCase(getGeneral.rejected, (state) => {
			state.general.status = RequestStatus.failed;
		});

		builder.addCase(getPerformance.pending, (state) => {
			state.performance.status = RequestStatus.loading;
		});
		builder.addCase(getPerformance.fulfilled, (state, action) => {
			state.performance.data = action.payload;
			state.performance.status = RequestStatus.still;
		});
		builder.addCase(getPerformance.rejected, (state) => {
			state.performance.status = RequestStatus.failed;
		});

		builder.addCase(getDataset.pending, (state) => {
			state.dataset.status = RequestStatus.loading;
		});
		builder.addCase(getDataset.fulfilled, (state, action) => {
			if (state.dataset.data.length > 0) {
				state.dataset.data = state.dataset.data.map((item: PeerCompanyDatasetDTO) => {
					const result = action.payload.find((it) => it.companyId === item.companyId);

					if (!result) return item;

					const mergedBoolean = [...(item.booleanDataPoints ?? []), ...(result.booleanDataPoints ?? [])].filter(
						(dataPoint, index, self) => self.findIndex((d) => Number(d.id) === Number(dataPoint.id)) === index,
					);

					const mergedNumeric = [...(item.numericDataPoints ?? []), ...(result.numericDataPoints ?? [])].filter(
						(dataPoint, index, self) => self.findIndex((d) => Number(d.id) === Number(dataPoint.id)) === index,
					);

					return {
						...item,
						booleanDataPoints: mergedBoolean,
						numericDataPoints: mergedNumeric,
					};
				});
			} else {
				state.dataset.data = action.payload;
			}
			state.dataset.status = RequestStatus.still;
		});
		builder.addCase(getDataset.rejected, (state) => {
			state.dataset.status = RequestStatus.failed;
		});

		builder.addCase(getFullDataset.pending, (state) => {
			state.fullDataset.status = RequestStatus.loading;
		});
		builder.addCase(getFullDataset.fulfilled, (state, action) => {
			state.fullDataset.data = action.payload;
			state.fullDataset.status = RequestStatus.still;
		});
		builder.addCase(getFullDataset.rejected, (state) => {
			state.fullDataset.status = RequestStatus.failed;
		});
	},
});

export const actionsBenchmark = {
	...slice.actions,
	getMetadata,
	getGeneral,
	getPerformance,
	getDataset,
	getFullDataset,
};
